import { useEffect, useState } from "react"

import { scrollToHashId } from "@basics/util-importless"


/**
 * Hook to use, when the component is mounted or not mounted.
 */
const useMounted = (): boolean => {
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  return mounted
}

/**
 * Hook to use, when the page should jump to a specific component/section of the page, which
 * is not immediately rendered, b/c e.g. a certain entity has to be loaded first.
 * The hashId will be extracted from the url.
 *
 * Uses MutationObserver to retry scrolling when the target element is not yet in the DOM.
 * This handles cases where child components render asynchronously (e.g., after API calls).
 *
 * @param literalToWaitFor hook will be executed again, if literalToWaitFor has changed
 * @param scrollIntoViewOptions behavior of the scrolling
 */
export const useScrollToHash = (
  literalToWaitFor: boolean | string | number,
  scrollIntoViewOptions: ScrollIntoViewOptions = { behavior: 'smooth', block: "center" }
): void => {
  const location = useWindowLocation()

  useEffect(() => {
    if (!location?.hash) return

    const targetId = location.hash.substring(1) // Remove leading '#'
    let observer: MutationObserver | null = null
    let timeoutId: NodeJS.Timeout | null = null

    const tryScroll = (): void => {
      const success = scrollToHashId(location.hash, scrollIntoViewOptions)
      if (success) {
        // Scroll successful - clean up observer
        observer?.disconnect()
        if (timeoutId) clearTimeout(timeoutId)
      }
    }

    // Try immediately
    tryScroll()

    // If not successful, observe DOM changes and retry when target appears
    if (typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(() => {
        const element = document.getElementById(targetId)
        if (element) {
          tryScroll()
        }
      })

      observer.observe(document.body, {
        childList: true,
        subtree: true
      })
    }

    // Safety timeout: Stop trying after 5 seconds
    timeoutId = setTimeout(() => {
      observer?.disconnect()
    }, 5000)

    return () => {
      observer?.disconnect()
      clearTimeout(timeoutId)
    }
  }, [location?.hash, literalToWaitFor])
}

/**
 * Listening to the location (url of the current visited page) => e.g. can be directly used in useEffect
 *
 * Should be used, when the current url is needed.
 */
export const useWindowLocation = (): Location => {
  const mounted = useMounted()
  const [location, setLocation] = useState<Location>(mounted ? window.location : undefined)

  useEffect(() => {
    // the window is defined and available after the component is mounted
    if (!mounted) return

    const setWindowLocation = () => {
      setLocation(window.location)
    }

    if (!location) {
      setWindowLocation()
    }

    // if the window changes, set the location
    window.addEventListener('windowLocationState', setWindowLocation)

    // when the component is unmounting, remove the listener
    return () => {
      window.removeEventListener('windowLocationState', setWindowLocation)
    }
  }, [mounted, location])

  return location
}

export interface WindowSize {
  width: number
  height: number
}

/**
 * Hook to use the size of the current window even while resizing
 * e.g. to adapt the size of embedded elements by resizing them
 *
 * usage:
 *
 * const windowSize = useWindowSize()
 * const width = windowSize.width
 */
export const useWindowSize = (): WindowSize => {
  // Initialize state with undefined width/height so server and client renders match
  // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
  const [windowSize, setWindowSize] = useState<WindowSize>({
    width: undefined,
    height: undefined,
  })
  useEffect(() => {
    // Handler to call on window resize
    const handleResize = () => {
      // Set window width/height to state
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }
    // Add event listener
    window.addEventListener("resize", handleResize)
    // Call handler right away so state gets updated with initial window size
    handleResize()
    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleResize)
  }, []) // Empty array ensures that effect is only run on mount

  return windowSize
}